import { QueryClient, QueryClientProvider, useMutation } from "@tanstack/react-query";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

import type { TargetFormat } from "./domain/subscription-converter.ts";
import {
  buildClashSnippet,
  buildProviderEndpoint,
  buildSurgeSnippet,
  providerKeyFromUrl,
} from "./features/converter/provider-url.ts";
import "./styles.css";

const queryClient = new QueryClient();

function RootLayout() {
  return (
    <div className="app-shell">
      <Outlet />
    </div>
  );
}

function ConverterPage() {
  const [subscriptionUrl, setSubscriptionUrl] = useState("");
  const [target, setTarget] = useState<TargetFormat>("clash");
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

  const origin = typeof window === "undefined" ? "https://example.com" : window.location.origin;
  const providerUrl = useMemo(
    () => buildProviderEndpoint({ origin, subscriptionUrl, target }),
    [origin, subscriptionUrl, target],
  );
  const providerKey = useMemo(() => providerKeyFromUrl(subscriptionUrl), [subscriptionUrl]);
  const snippet = useMemo(
    () =>
      target === "clash"
        ? buildClashSnippet(providerKey, providerUrl)
        : buildSurgeSnippet(providerKey, providerUrl),
    [providerKey, providerUrl, target],
  );

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(providerUrl);
      const text = await response.text();
      if (!response.ok) throw new Error(text || `HTTP ${response.status}`);
      return text;
    },
  });

  const canGenerate = subscriptionUrl.trim().length > 0;

  async function copy(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopiedLabel(label);
    window.setTimeout(() => setCopiedLabel(null), 1200);
  }

  return (
    <main className="converter">
      <header className="page-heading">
        <h1>订阅转换器</h1>
        <p>输入订阅地址，生成 Clash Provider 或 Surge External Group。</p>
      </header>

      <section className="form-section" aria-label="订阅转换表单">
        <label className="field">
          <span>订阅 URL</span>
          <input
            value={subscriptionUrl}
            onChange={(event) => setSubscriptionUrl(event.target.value)}
            placeholder="https://example.com/subscription.yaml"
            inputMode="url"
            autoComplete="url"
          />
        </label>

        <div className="format-row" role="radiogroup" aria-label="输出格式">
          <FormatButton active={target === "clash"} onClick={() => setTarget("clash")}>
            Clash Provider
          </FormatButton>
          <FormatButton active={target === "surge"} onClick={() => setTarget("surge")}>
            Surge External Group
          </FormatButton>
        </div>
      </section>

      <section className="results" aria-label="转换结果">
        <ResultBox
          title="Provider URL"
          value={canGenerate ? providerUrl : "输入订阅 URL 后生成"}
          disabled={!canGenerate}
          copied={copiedLabel === "provider"}
          onCopy={() => copy(providerUrl, "provider")}
        />

        <ResultBox
          title="配置片段"
          value={canGenerate ? snippet : "输入订阅 URL 后生成"}
          disabled={!canGenerate}
          copied={copiedLabel === "snippet"}
          onCopy={() => copy(snippet, "snippet")}
          multiline
        />

        <button
          className="preview-button"
          type="button"
          disabled={!canGenerate || previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
        >
          {previewMutation.isPending ? "转换中..." : "预览转换结果"}
        </button>

        {previewMutation.isError && (
          <pre className="preview preview-error">{previewMutation.error.message}</pre>
        )}
        {previewMutation.isSuccess && <pre className="preview">{previewMutation.data}</pre>}
      </section>
    </main>
  );
}

function FormatButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className={active ? "format-button format-button-active" : "format-button"}
      type="button"
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ResultBox({
  title,
  value,
  disabled,
  copied,
  multiline = false,
  onCopy,
}: {
  title: string;
  value: string;
  disabled: boolean;
  copied: boolean;
  multiline?: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="result-box">
      <div className="result-header">
        <strong>{title}</strong>
        <button type="button" disabled={disabled} onClick={onCopy}>
          {copied ? "已复制" : "复制"}
        </button>
      </div>
      {multiline ? <pre>{value}</pre> : <code>{value}</code>}
    </div>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: ConverterPage,
});
const routeTree = rootRoute.addChildren([indexRoute]);
const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
